// client/src/components/Insights/shared/InsightsFilterContext.jsx
// Global date-range filter for the Keeper AI Insights page.
// Persists selection to localStorage so it survives a reload.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'sk_insights_filter';

const fmt = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const todayStr = () => fmt(new Date());

const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return fmt(d);
};

const monthsAgoStr = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return fmt(d);
};

export const PRESETS = {
  '7d':  () => ({ from: daysAgoStr(7),    to: todayStr() }),
  '30d': () => ({ from: daysAgoStr(30),   to: todayStr() }),
  '90d': () => ({ from: daysAgoStr(90),   to: todayStr() }),
  '6m':  () => ({ from: monthsAgoStr(6),  to: todayStr() }),
  '12m': () => ({ from: monthsAgoStr(12), to: todayStr() }),
  'ytd': () => {
    const now = new Date();
    return { from: `${now.getFullYear()}-01-01`, to: todayStr() };
  },
};

export const PRESET_KEYS = ['7d', '30d', '90d', '6m', '12m', 'ytd', 'custom'];

const loadInitial = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // A preset like "last 30 days" moves with today; only custom ranges keep their dates.
      if (parsed && parsed.preset && parsed.preset !== 'custom' && PRESETS[parsed.preset]) {
        return { ...PRESETS[parsed.preset](), preset: parsed.preset };
      }
      if (parsed && parsed.from && parsed.to) {
        return {
          from: parsed.from,
          to: parsed.to,
          preset: 'custom',
        };
      }
    }
  } catch (_) { /* ignore */ }
  // default: last 30 days
  const def = PRESETS['30d']();
  return { ...def, preset: '30d' };
};

const InsightsFilterContext = createContext(null);

export const InsightsFilterProvider = ({ children }) => {
  const [{ from, to, preset }, setState] = useState(loadInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ from, to, preset }));
    } catch (_) { /* ignore */ }
  }, [from, to, preset]);

  const setFrom = useCallback((v) => setState((s) => ({ ...s, from: v, preset: 'custom' })), []);
  const setTo   = useCallback((v) => setState((s) => ({ ...s, to: v,   preset: 'custom' })), []);

  const setRange = useCallback((nextFrom, nextTo, nextPreset = 'custom') => {
    setState({ from: nextFrom, to: nextTo, preset: nextPreset });
  }, []);

  const setPreset = useCallback((key) => {
    if (key === 'custom') {
      setState((s) => ({ ...s, preset: 'custom' }));
      return;
    }
    const fn = PRESETS[key];
    if (!fn) return;
    const { from: f, to: t } = fn();
    setState({ from: f, to: t, preset: key });
  }, []);

  const value = useMemo(() => ({
    from, to, preset,
    setFrom, setTo, setRange, setPreset,
  }), [from, to, preset, setFrom, setTo, setRange, setPreset]);

  return (
    <InsightsFilterContext.Provider value={value}>
      {children}
    </InsightsFilterContext.Provider>
  );
};

export const useInsightsFilter = () => {
  const ctx = useContext(InsightsFilterContext);
  if (!ctx) {
    // Safe fallback when used outside the provider (e.g. older callers)
    const def = PRESETS['30d']();
    return {
      from: def.from,
      to: def.to,
      preset: '30d',
      setFrom: () => {}, setTo: () => {}, setRange: () => {}, setPreset: () => {},
    };
  }
  return ctx;
};

export default InsightsFilterContext;
